import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, Pause, Play, Trash2, CreditCard, Pencil } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";

export default function AdminConsole() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [planOpen, setPlanOpen] = useState(false);
  const [plan, setPlan] = useState({});

  const { data: companies = [] } = useQuery({ queryKey: ["admin-companies"], queryFn: async () => (await api.get("/admin/companies")).data });
  const { data: plans = [] } = useQuery({ queryKey: ["admin-plans"], queryFn: async () => (await api.get("/admin/plans")).data });

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const saveCompany = async () => {
    try {
      if (form.id) {
        const { id, n_strutture, n_utenti, n_ospiti, admin_email, admin_password, ...payload } = form;
        await api.put(`/admin/companies/${id}`, payload);
        toast.success("Azienda aggiornata");
      } else {
        await api.post("/admin/companies", form);
        toast.success("Azienda creata");
      }
      setOpen(false); setForm({});
      qc.invalidateQueries({ queryKey: ["admin-companies"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const toggle = async (c) => {
    const stato = c.stato === "sospeso" ? "attivo" : "sospeso";
    await api.put(`/admin/companies/${c.id}`, { stato });
    toast.success(stato === "sospeso" ? "Azienda sospesa" : "Azienda riattivata");
    qc.invalidateQueries({ queryKey: ["admin-companies"] });
  };
  const savePlan = async () => {
    await api.post("/admin/plans", { ...plan, prezzo: Number(plan.prezzo), max_strutture: Number(plan.max_strutture), max_utenti: Number(plan.max_utenti) });
    toast.success("Piano creato"); setPlanOpen(false); setPlan({});
    qc.invalidateQueries({ queryKey: ["admin-plans"] });
  };
  const delPlan = async (id) => { await api.delete(`/admin/plans/${id}`); qc.invalidateQueries({ queryKey: ["admin-plans"] }); };

  return (
    <div>
      <h1 className="text-2xl font-head font-bold mb-1">Console Super Admin</h1>
      <p className="text-sm text-muted-foreground mb-6">Gestione aziende clienti e piani di abbonamento</p>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head font-bold flex items-center gap-2"><Building2 size={18} /> Aziende clienti ({companies.length})</h2>
        <Button onClick={() => { setForm({ stato: "attivo" }); setOpen(true); }} data-testid="new-company" className="font-semibold"><Plus size={18} className="mr-1" /> Nuova azienda</Button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {[...companies].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it")).map((c) => (
          <div key={c.id} className="gr-card p-5" data-testid={`company-${c.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-head font-bold text-lg">{c.nome}</div>
                {c.ragione_sociale && <div className="text-xs text-muted-foreground">{c.ragione_sociale}</div>}
                <div className="text-xs text-muted-foreground">Piano {c.piano || "—"}{c.piva ? ` · P.IVA ${c.piva}` : ""}</div></div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setForm(c); setOpen(true); }} data-testid={`edit-${c.id}`}><Pencil size={14} /></Button>
                <StatusBadge type="stato" value={c.stato === "sospeso" ? "sospeso" : "attivo"} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 my-4 text-center">
              <div className="p-2 rounded-lg bg-secondary"><div className="font-bold tabnum">{c.n_strutture}</div><div className="text-xs text-muted-foreground">Strutture</div></div>
              <div className="p-2 rounded-lg bg-secondary"><div className="font-bold tabnum">{c.n_utenti}</div><div className="text-xs text-muted-foreground">Utenti</div></div>
              <div className="p-2 rounded-lg bg-secondary"><div className="font-bold tabnum">{c.n_ospiti}</div><div className="text-xs text-muted-foreground">Ospiti</div></div>
            </div>
            <Button size="sm" variant={c.stato === "sospeso" ? "default" : "outline"} className="w-full" onClick={() => toggle(c)} data-testid={`toggle-${c.id}`}>
              {c.stato === "sospeso" ? <><Play size={15} className="mr-1" /> Riattiva</> : <><Pause size={15} className="mr-1" /> Sospendi</>}
            </Button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head font-bold flex items-center gap-2"><CreditCard size={18} /> Piani di abbonamento</h2>
        <Button variant="outline" onClick={() => { setPlan({}); setPlanOpen(true); }} data-testid="new-plan"><Plus size={16} className="mr-1" /> Nuovo piano</Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="gr-card p-5" data-testid={`plan-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="font-head font-bold text-lg">{p.nome}</div>
              <Button size="sm" variant="ghost" onClick={() => delPlan(p.id)}><Trash2 size={14} className="text-destructive" /></Button>
            </div>
            <div className="text-2xl font-head font-bold text-primary tabnum my-2">{fmtEur(p.prezzo)}<span className="text-sm text-muted-foreground font-normal">/mese</span></div>
            <div className="text-sm text-muted-foreground">Fino a {p.max_strutture} strutture · {p.max_utenti} utenti</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Modifica azienda" : "Nuova azienda cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">Nome azienda (commerciale)</Label><Input value={form.nome || ""} onChange={(e) => setF("nome", e.target.value)} data-testid="company-nome" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Piano</Label>
              <Select value={form.piano || ""} onValueChange={(v) => setF("piano", v)}>
                <SelectTrigger data-testid="company-piano"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.nome}>{p.nome} — {fmtEur(p.prezzo)}/mese</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="pt-2 border-t">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Dati fiscali (intestatario contratto)</div>
              <div className="space-y-3">
                <div><Label className="text-xs font-semibold mb-1 block">Ragione sociale</Label><Input value={form.ragione_sociale || ""} onChange={(e) => setF("ragione_sociale", e.target.value)} placeholder="Es. Orto della Longevità S.r.l." data-testid="company-ragione-sociale" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold mb-1 block">Partita IVA</Label><Input value={form.piva || ""} onChange={(e) => setF("piva", e.target.value)} data-testid="company-piva" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">Codice Fiscale</Label><Input value={form.codice_fiscale || ""} onChange={(e) => setF("codice_fiscale", e.target.value)} data-testid="company-cf" /></div>
                </div>
                <div><Label className="text-xs font-semibold mb-1 block">Indirizzo sede legale</Label><Input value={form.sede_indirizzo || ""} onChange={(e) => setF("sede_indirizzo", e.target.value)} data-testid="company-sede-indirizzo" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Città</Label><Input value={form.sede_citta || ""} onChange={(e) => setF("sede_citta", e.target.value)} data-testid="company-sede-citta" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">CAP</Label><Input value={form.sede_cap || ""} onChange={(e) => setF("sede_cap", e.target.value)} data-testid="company-sede-cap" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold mb-1 block">Codice SDI</Label><Input value={form.sdi || ""} onChange={(e) => setF("sdi", e.target.value)} data-testid="company-sdi" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">PEC</Label><Input value={form.pec || ""} onChange={(e) => setF("pec", e.target.value)} data-testid="company-pec" /></div>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Referente amministrativo</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Nome referente</Label><Input value={form.referente_nome || ""} onChange={(e) => setF("referente_nome", e.target.value)} data-testid="company-referente-nome" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Email fatturazione</Label><Input value={form.referente_email || ""} onChange={(e) => setF("referente_email", e.target.value)} data-testid="company-referente-email" /></div>
              </div>
            </div>

            {!form.id && (
              <div className="pt-2 border-t">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Account admin iniziale</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold mb-1 block">Email admin</Label><Input value={form.admin_email || ""} onChange={(e) => setF("admin_email", e.target.value)} data-testid="company-admin-email" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">Password admin</Label><Input value={form.admin_password || ""} onChange={(e) => setF("admin_password", e.target.value)} placeholder="demo1234" /></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={saveCompany} data-testid="save-company" className="font-semibold">{form.id ? "Salva modifiche" : "Crea azienda"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo piano</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Nome</Label><Input value={plan.nome || ""} onChange={(e) => setPlan((p) => ({ ...p, nome: e.target.value }))} data-testid="plan-nome" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Prezzo €/mese</Label><Input type="number" value={plan.prezzo || ""} onChange={(e) => setPlan((p) => ({ ...p, prezzo: e.target.value }))} data-testid="plan-prezzo" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Max strutture</Label><Input type="number" value={plan.max_strutture || ""} onChange={(e) => setPlan((p) => ({ ...p, max_strutture: e.target.value }))} /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Max utenti</Label><Input type="number" value={plan.max_utenti || ""} onChange={(e) => setPlan((p) => ({ ...p, max_utenti: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPlanOpen(false)}>Annulla</Button><Button onClick={savePlan} data-testid="save-plan" className="font-semibold">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
